/**
 * Renders a self-contained badge image (as an inline SVG `data:` URI) for an
 * achievement tier. Mirrors the mobile achievement badge (hexagon + star, tier
 * gradient) so the dashboard and player app read as the same system. Used to
 * seed the static achievement fixtures until real logos come from the backend;
 * `resolveSystemIcon` treats `data:` URIs as remote and returns them untouched,
 * so these render like any uploaded logo.
 */

export type TAchievementTier = 'silver' | 'gold' | 'diamond'

type TTierStyle = {
  label: string
  /** gradient start (top-left) */
  start: string
  /** gradient end (bottom-right) */
  end: string
  /** star + stroke accent */
  accent: string
}

// Tier colors match the mobile badge themes (achievement-logo.tsx): Gümüş /
// Altın / Elmas.
export const ACHIEVEMENT_TIERS: Record<TAchievementTier, TTierStyle> = {
  silver: {
    label: 'Gümüş',
    start: '#E2E8F0',
    end: '#64748B',
    accent: '#F8FAFC',
  },
  gold: {
    label: 'Altın',
    start: '#FDE047',
    end: '#F97316',
    accent: '#FEF3C7',
  },
  diamond: {
    label: 'Elmas',
    start: '#A855F7',
    end: '#22D3EE',
    accent: '#F5D0FE',
  },
}

// lucide "star" path (24×24 viewBox), rendered as an outline like the mobile
// badge. Centered + scaled to ~34px inside the 100×100 badge.
const STAR_PATH =
  'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.79 21.28a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.554 9.87a.53.53 0 0 1 .294-.904l5.165-.756a2.122 2.122 0 0 0 1.597-1.16z'

export function achievementMedalDataUri(tier: TAchievementTier): string {
  const s = ACHIEVEMENT_TIERS[tier]
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 100 100">
  <defs>
    <linearGradient id="g" x1="12" y1="10" x2="88" y2="90" gradientUnits="userSpaceOnUse">
      <stop offset="0" stop-color="${s.start}"/>
      <stop offset="1" stop-color="${s.end}"/>
    </linearGradient>
  </defs>
  <circle cx="50" cy="50" r="45" fill="${s.start}22" stroke="${s.accent}66" stroke-width="1.5"/>
  <polygon points="50,7 86,28 86,72 50,93 14,72 14,28" fill="url(#g)" stroke="${s.accent}" stroke-width="2.4" stroke-linejoin="round"/>
  <polygon points="50,18 77,34 77,66 50,82 23,66 23,34" fill="rgba(28,24,62,0.5)" stroke="rgba(255,255,255,0.28)" stroke-width="1.3" stroke-linejoin="round"/>
  <path d="${STAR_PATH}" fill="none" stroke="${s.accent}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" transform="translate(33 33) scale(1.41667)"/>
</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}
